'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["order", "children", "direction", "onOrderChange", "DragList"];
import PropTypes from 'prop-types';
import { Children, Component } from 'react';
import { findDOMNode } from 'react-dom';
import invariant from 'react-utils/invariant';
import { callIfPossible } from 'foundations-components/transitional/utils/functions';
import UIDragList from './UIDragList';
import { jsx as _jsx } from "react/jsx-runtime";
class UISortableList extends Component {
  constructor(_props) {
    super(_props);
    this.updateOrder = order => {
      this.setState({
        order
      });
    };
    this.getHoverMiddleCoordinate = ({
      direction
    }, {
      hoverBoundingRect
    }) => {
      if (direction === 'horizontal') {
        return (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      }
      return (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
    };
    this.getHoverClientCoordinate = ({
      direction
    }, {
      clientOffset,
      hoverBoundingRect
    }) => {
      if (direction === 'horizontal') {
        return clientOffset.x - hoverBoundingRect.left;
      }
      return clientOffset.y - hoverBoundingRect.top;
    };
    this.handleOrderChange = (dragId, hoverId) => {
      const {
        order
      } = this.state;
      const newOrder = order.slice(0);
      const dragIndex = newOrder.indexOf(dragId);
      const hoverIndex = newOrder.indexOf(hoverId);
      newOrder.splice(dragIndex, 1);
      const leftSide = newOrder.slice(0, hoverIndex);
      const rightSide = newOrder.slice(hoverIndex);
      this.updateOrder([...leftSide, dragId, ...rightSide]);
    };
    this.handleDrop = () => {
      const {
        onOrderChange
      } = this.props;
      const {
        order
      } = this.state;
      callIfPossible(onOrderChange, order);
    };
    this.handleHover = (props, monitor, component) => {
      const {
        order
      } = this.state;
      const {
        direction
      } = props;
      const dragId = monitor.getItem().dragId;
      const hoverId = props.dragId;
      const dragIndex = order.indexOf(dragId);
      const hoverIndex = order.indexOf(hoverId);
      if (dragIndex === hoverIndex) {
        return;
      }

      // eslint-disable-next-line react/no-find-dom-node
      const hoverBoundingRect = findDOMNode(component).getBoundingClientRect();
      const hoverMiddleCoordinate = this.getHoverMiddleCoordinate({
        direction
      }, {
        hoverBoundingRect
      });
      const clientOffset = monitor.getClientOffset();
      const hoverClientCoordinate = this.getHoverClientCoordinate({
        direction
      }, {
        clientOffset,
        hoverBoundingRect
      });
      const indexIsBelow = dragIndex < hoverIndex;
      const hoverOnNextHalf = hoverClientCoordinate < hoverMiddleCoordinate;
      if (indexIsBelow && hoverOnNextHalf) {
        return;
      }
      const indexIsAbove = dragIndex > hoverIndex;
      const hoverOnPriorHalf = hoverClientCoordinate > hoverMiddleCoordinate;
      if (indexIsAbove && hoverOnPriorHalf) {
        return;
      }
      this.handleOrderChange(dragId, hoverId);
    };
    const {
      order: _order
    } = _props;
    this.state = {
      order: _order
    };
  }
  UNSAFE_componentWillReceiveProps({
    order
  }) {
    this.setState({
      order
    });
  }
  render() {
    const _this$props = this.props,
      {
        children,
        DragList
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      order
    } = this.state;
    const arrayOfChildren = Children.toArray(children);
    const orderedChildren = arrayOfChildren.reduce((acc, child) => {
      const index = order.indexOf(child.props.dragId);
      invariant(index !== -1, 'All ids in `order` must match a child with the value id as `dragId`');
      acc[index] = child;
      return acc;
    }, order.map(() => null));
    invariant(orderedChildren.indexOf(null) === -1, 'All ids in `order` must match a child with the same value as `dragId`');
    return /*#__PURE__*/_jsx(DragList, Object.assign({
      onDndHover: this.handleHover,
      onDndDrop: this.handleDrop,
      onEndDrag: this.handleDrop
    }, rest, {
      children: orderedChildren
    }));
  }
}
UISortableList.propTypes = {
  direction: PropTypes.oneOf(['horizontal', 'vertical']),
  order: PropTypes.arrayOf(PropTypes.string),
  onOrderChange: PropTypes.func,
  children: PropTypes.node,
  withDragDropContext: PropTypes.bool,
  DragList: PropTypes.elementType.isRequired
};
UISortableList.defaultProps = {
  direction: 'vertical',
  DragList: UIDragList
};
export default UISortableList;